import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { IonItem, IonLabel, IonTextarea } from '@ionic/react';

import Picker from 'components/Picker';
import Typography from 'components/Typography';
import { useDateFormat } from 'intl';
import { useQuery } from 'query';
import { reasonsIO } from 'scenes/FinancePage/IO/sources';

export interface ConfirmDialogConfig {
  modalTitle?: string;
  onSubmit: (data?: string | null | undefined | number, role?: string) => void;
  onClose: (data?: string | null | undefined | number, role?: string) => void;
  businessPartner: { name: string; id: number };
  period: string[];
  createdBy: { name: string; id: number };
  parentIo: number | null;
}

const ConfirmDialog = ({
  modalTitle = '',
  onSubmit,
  onClose,
  businessPartner,
  period,
  createdBy,
  parentIo,
}: ConfirmDialogConfig) => {
  const [reasons, setReasons] = useState([]);
  const [comment, setComment] = useState('');
  const [errors, setErrors] = useState({
    reasons: '',
  });

  const { data, isLoading, error } = useQuery(['reasons'], () => reasonsIO());

  const handleChangeReasons = useCallback((_, { id }) => {
    return setReasons((state: any) =>
      state.includes(id)
        ? state.filter((v: number) => v !== id)
        : [...state, id]
    );
  }, []);

  const isSelectedReason = useCallback(
    ({ id }: { id: never }) => reasons.includes(id),
    [reasons]
  );

  const handleChangeComment = useCallback(e => setComment(e.detail.value), []);

  const validate = (values: { reasons: number[] }) => {
    const errors = {
      reasons: '',
    };

    if (!values.reasons.length) {
      errors.reasons = 'Please choose reason';
    }

    return errors;
  };

  const handleSubmit = () => {
    const errors = validate({ reasons });
    if (errors.reasons.length) {
      setErrors(errors);
    } else {
      onSubmit({
        reasons,
        comment,
      } as any);
    }
  };

  useEffect(() => {
    if (errors.reasons.length) {
      setErrors(validate({ reasons }));
    }
  }, [reasons]);

  const dateFormat = useDateFormat({ timeZone: 'utc', template: 'MONTH' });

  const entityName = parentIo ? 'Amendment' : 'IO';
  const from = dateFormat(period?.[0]);
  const to = dateFormat(period?.[1]);
  const periodRange = from === to ? from : `${from} - ${to}`;
  const subtitle = useMemo(() => {
    return `${businessPartner?.name} / ${periodRange} by ${
      createdBy?.name || createdBy
    }`;
  }, [businessPartner, periodRange, createdBy]);

  return (
    <Picker
      label={'Reason'}
      data={data}
      isLoading={isLoading}
      error={error}
      title={modalTitle}
      toolbarTitle={entityName}
      toolbarSubtitle={subtitle}
      onSubmit={handleSubmit}
      onClose={onClose}
      onItemClick={handleChangeReasons}
      isSelected={isSelectedReason}
      apply={true}
      DataListProps={{ virtualized: false }}
      withSearchbar={false}
    >
      {errors.reasons ? (
        <Typography color={'danger'} className={'ion-padding'}>
          {errors.reasons}
        </Typography>
      ) : null}

      <IonItem className={'padding-top'}>
        <IonLabel className={'label-full-width'}>
          <Typography variant={'caption'}>Comment</Typography>
          <IonTextarea
            placeholder={'Comment text...'}
            onIonChange={handleChangeComment}
            autoGrow={true}
          />
        </IonLabel>
      </IonItem>
    </Picker>
  );
};

export default ConfirmDialog;
